import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import { rootReducer } from '@verantos-dev/core-annotation-tool';

import { DATA_IMPORT_MODES } from '../constants';
import { AnnotatorResponse, SupervisorResponse } from '../types/response';

export interface RequiredAttribute {
  id: string;
  label: string;
}

export interface AddedConcept {
  conceptCardId: string; // Frontend-only ID for UI management
  conceptGroupId?: string; // Backend ID from getConceptGroups
  conceptGroupName: string;
  requiredAttributes: RequiredAttribute[];
  inEditMode: boolean;
}

export interface AllocationData {
  trainingAllocationCount: number;
  testAllocationCount: number;
  validationAllocationCount: number;
}

export interface AllocationPercentage {
  trainingPercentage: number;
  testPercentage: number;
  validationPercentage: number;
}

export interface AnnotationUser extends AnnotatorResponse {
  allocationPercentage?: AllocationPercentage;
}

export interface ConditionArea {
  id: string;
  label: string;
}

export interface HealthSystem {
  id: string;
  label: string;
}

// TODO: cleanup with cp_annotation_createproject_agent ff
export interface AgentConcept {
  sequence: number;
  name: string;
  synonyms: string[];
  notes: string;
}

export interface AgentSection {
  id: string;
  heading: string;
  body: string;
}

export interface AnnotationAgentState {
  enabled: boolean;
  modelId: string | null;
  concepts: Record<string /* conceptGroupId */, AgentConcept>;
  nextSequence: number;
  // Saved custom prompt sections in insertion order. Drafts live in the section cards.
  sections: AgentSection[];
}

export type AgentConceptsSnapshot = Pick<AnnotationAgentState, 'concepts' | 'nextSequence'>;

export interface CreateProjectState {
  activeStep: number;
  conditionArea: ConditionArea | null;
  healthSystem: HealthSystem | null;
  projectName: string;
  addedConcepts: AddedConcept[];
  workflowId: string | null;
  selectedAnnotators: AnnotationUser[];
  selectedSupervisors: SupervisorResponse[];
  defaultAllocationMode: boolean;
  datasetImportMode: string;
  datasplitImportMode: string;
  uploadedNotesParquetFileName: string | null;
  uploadedPersonParquetFileName: string | null;
  uploadedVisitParquetFileName: string | null;
  notesS3FilePath: string | null;
  personS3FilePath: string | null;
  visitS3FilePath: string | null;
  uploadedDatasplittingJSONFileName: string | null;
  datasplitJsonS3FilePath: string | null;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  // Release flag value captured once per wizard session, so the step list cannot
  // change under activeStep while the wizard is open. null until captured.
  isAgentStepEnabled: boolean | null;
  annotationAgent: AnnotationAgentState;
  // Custom allocation with the agent on: each human's share as typed, keyed by annotator ID.
  // A human without an entry has not been given one yet.
  humanSharePercentages: Record<string, string>;
}

const initialAnnotationAgentState: AnnotationAgentState = {
  enabled: false,
  modelId: null,
  concepts: {},
  nextSequence: 1,
  sections: [],
};

const initialState: CreateProjectState = {
  activeStep: 0,
  conditionArea: null,
  healthSystem: null,
  projectName: '',
  addedConcepts: [],
  workflowId: null,
  selectedAnnotators: [],
  selectedSupervisors: [],
  defaultAllocationMode: true,
  datasetImportMode: DATA_IMPORT_MODES[0],
  datasplitImportMode: DATA_IMPORT_MODES[0],
  uploadedNotesParquetFileName: null,
  uploadedPersonParquetFileName: null,
  uploadedVisitParquetFileName: null,
  notesS3FilePath: null,
  personS3FilePath: null,
  visitS3FilePath: null,
  uploadedDatasplittingJSONFileName: null,
  datasplitJsonS3FilePath: null,
  isAgentStepEnabled: null,
  annotationAgent: initialAnnotationAgentState,
  humanSharePercentages: {},
};

export const createProjectSlice = createSlice({
  name: 'createProject',
  initialState,
  reducers: {
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },
    clearActiveStep: (state) => {
      state.activeStep = 0;
    },
    setConditionArea: (state, action: PayloadAction<ConditionArea>) => {
      state.conditionArea = action.payload;
    },
    setHealthSystem: (state, action: PayloadAction<HealthSystem>) => {
      state.healthSystem = action.payload;
    },
    setProjectName: (state, action: PayloadAction<string>) => {
      state.projectName = action.payload;
    },
    clearConditionArea: (state) => {
      state.conditionArea = null;
    },
    clearHealthSystem: (state) => {
      state.healthSystem = null;
    },
    clearProjectName: (state) => {
      state.projectName = '';
    },
    setAddedConcepts: (state, action: PayloadAction<AddedConcept[]>) => {
      state.addedConcepts = action.payload;
    },
    clearAddedConcepts: (state) => {
      state.addedConcepts = [];
    },
    addConcept: (state, action: PayloadAction<AddedConcept>) => {
      if (
        state.addedConcepts.some(
          (concept) => concept.conceptGroupName === action.payload.conceptGroupName
        )
      ) {
        return;
      }
      state.addedConcepts.push(action.payload);
    },
    removeConcept: (state, action: PayloadAction<string>) => {
      state.addedConcepts = state.addedConcepts.filter(
        (concept) => concept.conceptCardId !== action.payload
      );
    },
    setConceptGroupName: (state, action: PayloadAction<AddedConcept>) => {
      state.addedConcepts = state.addedConcepts.map((concept) =>
        concept.conceptCardId === action.payload.conceptCardId
          ? {
              ...concept,
              conceptGroupName: action.payload.conceptGroupName,
              conceptGroupId: action.payload.conceptGroupId,
            }
          : concept
      );
    },
    setConceptInEditMode: (state, action: PayloadAction<string>) => {
      state.addedConcepts = state.addedConcepts.filter((concept) =>
        concept.conceptGroupName.trim()
      );
      state.addedConcepts = state.addedConcepts.map((concept) => ({
        ...concept,
        inEditMode: false,
      }));
      state.addedConcepts = state.addedConcepts.map((concept) =>
        concept.conceptCardId === action.payload ? { ...concept, inEditMode: true } : concept
      );
    },
    clearConceptInEditMode: (state) => {
      state.addedConcepts = state.addedConcepts.filter((concept) =>
        concept.conceptGroupName.trim()
      );
      state.addedConcepts = state.addedConcepts.map((concept) => ({
        ...concept,
        inEditMode: false,
      }));
    },
    addRequiredAttribute: (
      state,
      action: PayloadAction<{ conceptId: string; attribute: RequiredAttribute }>
    ) => {
      if (
        state.addedConcepts.some(
          (concept) =>
            concept.conceptCardId === action.payload.conceptId &&
            concept.requiredAttributes.some((attr) => attr.id === action.payload.attribute.id)
        )
      ) {
        return;
      }
      state.addedConcepts = state.addedConcepts.map((concept) =>
        concept.conceptCardId === action.payload.conceptId
          ? {
              ...concept,
              requiredAttributes: [...concept.requiredAttributes, action.payload.attribute],
            }
          : concept
      );
    },
    removeRequiredAttribute: (
      state,
      action: PayloadAction<{ conceptId: string; attribute: RequiredAttribute }>
    ) => {
      if (
        !state.addedConcepts.some(
          (concept) =>
            concept.conceptCardId === action.payload.conceptId &&
            concept.requiredAttributes.some((attr) => attr.id === action.payload.attribute.id)
        )
      ) {
        return;
      }
      state.addedConcepts = state.addedConcepts.map((concept) =>
        concept.conceptCardId === action.payload.conceptId
          ? {
              ...concept,
              requiredAttributes: concept.requiredAttributes.filter(
                (attribute) => attribute.id !== action.payload.attribute.id
              ),
            }
          : concept
      );
    },
    clearAllWizardStates: (state) => {
      state.activeStep = 0;
      state.conditionArea = null;
      state.healthSystem = null;
      state.projectName = '';
      state.addedConcepts = [];
      state.workflowId = null;
      state.selectedAnnotators = [];
      state.selectedSupervisors = [];
      state.defaultAllocationMode = true;
      state.uploadedNotesParquetFileName = null;
      state.uploadedPersonParquetFileName = null;
      state.uploadedVisitParquetFileName = null;
      state.uploadedDatasplittingJSONFileName = null;
      state.datasetImportMode = DATA_IMPORT_MODES[0];
      state.datasplitImportMode = DATA_IMPORT_MODES[0];
      state.notesS3FilePath = null;
      state.personS3FilePath = null;
      state.visitS3FilePath = null;
      state.datasplitJsonS3FilePath = null;
      state.isAgentStepEnabled = null;
      state.annotationAgent = { ...initialAnnotationAgentState, sections: [] };
      state.humanSharePercentages = {};
    },
    // TODO: cleanup with cp_annotation_createproject_agent ff
    snapshotIsAgentStepEnabled: (state, action: PayloadAction<boolean>) => {
      // First capture wins: only clearAllWizardStates releases the snapshot.
      if (state.isAgentStepEnabled === null) {
        state.isAgentStepEnabled = action.payload;
      }
    },
    setAnnotationAgentEnabled: (state, action: PayloadAction<boolean>) => {
      state.annotationAgent.enabled = action.payload;
      // Turning the agent on asks for every Custom share again (VVA-3915).
      if (action.payload) {
        state.humanSharePercentages = {};
      }
    },
    setHumanSharePercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; value: string }>
    ) => {
      state.humanSharePercentages[action.payload.annotatorId] = action.payload.value;
    },
    setAnnotationAgentModelId: (state, action: PayloadAction<string>) => {
      state.annotationAgent.modelId = action.payload;
    },
    setAnnotationAgentConcepts: (state, action: PayloadAction<AgentConceptsSnapshot>) => {
      state.annotationAgent.concepts = action.payload.concepts;
      state.annotationAgent.nextSequence = action.payload.nextSequence;
    },
    // Writes the saved edits of a concept; its sequence, and so its alias, never changes.
    saveAnnotationAgentConcept: (
      state,
      action: PayloadAction<{
        conceptGroupId: string;
        name: string;
        synonyms: string[];
        notes: string;
      }>
    ) => {
      const { conceptGroupId, name, synonyms, notes } = action.payload;
      const concept = state.annotationAgent.concepts[conceptGroupId];
      if (!concept) return;
      concept.name = name;
      concept.synonyms = synonyms;
      concept.notes = notes;
    },
    // Appends a new section, or replaces the heading and body of one that is already saved,
    // keeping its position.
    saveAnnotationAgentSection: (state, action: PayloadAction<AgentSection>) => {
      const existing = state.annotationAgent.sections.find(
        (section) => section.id === action.payload.id
      );
      if (existing) {
        existing.heading = action.payload.heading;
        existing.body = action.payload.body;
        return;
      }
      state.annotationAgent.sections.push(action.payload);
    },
    removeAnnotationAgentSection: (state, action: PayloadAction<string>) => {
      state.annotationAgent.sections = state.annotationAgent.sections.filter(
        (section) => section.id !== action.payload
      );
    },
    setWorkflowId: (state, action: PayloadAction<string>) => {
      state.workflowId = action.payload;
    },
    clearWorkflowId: (state) => {
      state.workflowId = null;
    },
    setSelectedAnnotators: (state, action: PayloadAction<AnnotatorResponse[]>) => {
      state.selectedAnnotators = action.payload;
    },
    addSelectedAnnotator: (state, action: PayloadAction<AnnotatorResponse>) => {
      if (state.selectedAnnotators.some((annotator) => annotator.id === action.payload.id)) {
        return;
      }
      state.selectedAnnotators.push(action.payload);
      // TODO: cleanup with cp_annotation_createproject_agent ff
      // Adding a person asks for every Custom share again, so the manager sets them all
      // (VVA-3915). Only the agent's Custom allocation reads these.
      state.humanSharePercentages = {};
    },
    removeSelectedAnnotator: (state, action: PayloadAction<string>) => {
      if (!state.selectedAnnotators.some((annotator) => annotator.id === action.payload)) {
        return;
      }
      state.selectedAnnotators = state.selectedAnnotators.filter(
        (annotator) => annotator.id !== action.payload
      );
      // TODO: cleanup with cp_annotation_createproject_agent ff
      delete state.humanSharePercentages[action.payload];
    },
    clearSelectedAnnotators: (state) => {
      state.selectedAnnotators = [];
    },
    setSelectedSupervisors: (state, action: PayloadAction<SupervisorResponse[]>) => {
      state.selectedSupervisors = action.payload;
    },
    addSelectedSupervisor: (state, action: PayloadAction<SupervisorResponse>) => {
      if (state.selectedSupervisors.some((supervisor) => supervisor.id === action.payload.id)) {
        return;
      }
      state.selectedSupervisors.push(action.payload);
    },
    removeSelectedSupervisor: (state, action: PayloadAction<string>) => {
      if (!state.selectedSupervisors.some((supervisor) => supervisor.id === action.payload)) {
        return;
      }
      state.selectedSupervisors = state.selectedSupervisors.filter(
        (supervisor) => supervisor.id !== action.payload
      );
    },
    clearSelectedSupervisors: (state) => {
      state.selectedSupervisors = [];
    },
    setDefaultAllocationMode: (state) => {
      state.defaultAllocationMode = true;
    },
    clearDefaultAllocationMode: (state) => {
      state.defaultAllocationMode = false;
    },
    setDatasetImportMode: (state, action: PayloadAction<(typeof DATA_IMPORT_MODES)[number]>) => {
      state.datasetImportMode = action.payload;
    },
    setDatasplitImportMode: (state, action: PayloadAction<(typeof DATA_IMPORT_MODES)[number]>) => {
      state.datasplitImportMode = action.payload;
    },
    handleChangeTrainingPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; trainingPercentage: number }>
    ) => {
      if (
        !state.selectedAnnotators.some((annotator) => annotator.id === action.payload.annotatorId)
      ) {
        return;
      }
      state.selectedAnnotators = state.selectedAnnotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: action.payload.trainingPercentage,
              testPercentage: existing?.testPercentage ?? 0,
              validationPercentage: existing?.validationPercentage ?? 0,
            },
          };
        }
        return annotator;
      });
    },
    handleChangeTestPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; testPercentage: number }>
    ) => {
      if (
        !state.selectedAnnotators.some((annotator) => annotator.id === action.payload.annotatorId)
      ) {
        return;
      }
      state.selectedAnnotators = state.selectedAnnotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: existing?.trainingPercentage ?? 0,
              testPercentage: action.payload.testPercentage,
              validationPercentage: existing?.validationPercentage ?? 0,
            },
          };
        }
        return annotator;
      });
    },
    handleChangeValidationPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; validationPercentage: number }>
    ) => {
      if (
        !state.selectedAnnotators.some((annotator) => annotator.id === action.payload.annotatorId)
      ) {
        return;
      }
      state.selectedAnnotators = state.selectedAnnotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: existing?.trainingPercentage ?? 0,
              testPercentage: existing?.testPercentage ?? 0,
              validationPercentage: action.payload.validationPercentage,
            },
          };
        }
        return annotator;
      });
    },
    setUploadedNotesParquetFileName: (state, action: PayloadAction<string>) => {
      state.uploadedNotesParquetFileName = action.payload;
    },
    clearUploadedNotesParquetFileName: (state) => {
      state.uploadedNotesParquetFileName = null;
    },
    setUploadedPersonParquetFileName: (state, action: PayloadAction<string>) => {
      state.uploadedPersonParquetFileName = action.payload;
    },
    clearUploadedPersonParquetFileName: (state) => {
      state.uploadedPersonParquetFileName = null;
    },
    setUploadedVisitParquetFileName: (state, action: PayloadAction<string>) => {
      state.uploadedVisitParquetFileName = action.payload;
    },
    clearUploadedVistParquetFileName: (state) => {
      state.uploadedVisitParquetFileName = null;
    },
    setNotesS3FilePath: (state, action: PayloadAction<string>) => {
      state.notesS3FilePath = action.payload;
    },
    clearNotesS3FilePath: (state) => {
      state.notesS3FilePath = null;
    },
    setPersonS3FilePath: (state, action: PayloadAction<string>) => {
      state.personS3FilePath = action.payload;
    },
    clearPersonS3FilePath: (state) => {
      state.personS3FilePath = null;
    },
    setVisitS3FilePath: (state, action: PayloadAction<string>) => {
      state.visitS3FilePath = action.payload;
    },
    clearVisitS3FilePath: (state) => {
      state.visitS3FilePath = null;
    },
    setUploadedDatasplittingJSONFileName: (state, action: PayloadAction<string>) => {
      state.uploadedDatasplittingJSONFileName = action.payload;
    },
    clearUploadedDatasplittingJSONFileName: (state) => {
      state.uploadedDatasplittingJSONFileName = null;
    },

    setDatasplitJsonS3FilePath: (state, action: PayloadAction<string>) => {
      state.datasplitJsonS3FilePath = action.payload;
    },
    clearDatasplitJsonS3FilePath: (state) => {
      state.datasplitJsonS3FilePath = null;
    },
  },
});

export const {
  setActiveStep,
  clearActiveStep,
  setConditionArea,
  setHealthSystem,
  setProjectName,
  setAddedConcepts,
  clearAddedConcepts,
  clearConditionArea,
  clearHealthSystem,
  clearProjectName,
  addConcept,
  removeConcept,
  setConceptInEditMode,
  clearConceptInEditMode,
  addRequiredAttribute,
  removeRequiredAttribute,
  setConceptGroupName,
  clearAllWizardStates,
  setWorkflowId,
  clearWorkflowId,
  setSelectedAnnotators,
  clearSelectedAnnotators,
  addSelectedAnnotator,
  removeSelectedAnnotator,
  setSelectedSupervisors,
  clearSelectedSupervisors,
  addSelectedSupervisor,
  removeSelectedSupervisor,
  setDefaultAllocationMode,
  clearDefaultAllocationMode,
  handleChangeTrainingPercentage,
  handleChangeTestPercentage,
  handleChangeValidationPercentage,
  setUploadedNotesParquetFileName,
  clearUploadedNotesParquetFileName,
  setUploadedPersonParquetFileName,
  clearUploadedPersonParquetFileName,
  setUploadedVisitParquetFileName,
  clearUploadedVistParquetFileName,
  setNotesS3FilePath,
  clearNotesS3FilePath,
  setPersonS3FilePath,
  clearPersonS3FilePath,
  setVisitS3FilePath,
  clearVisitS3FilePath,
  setUploadedDatasplittingJSONFileName,
  clearUploadedDatasplittingJSONFileName,
  setDatasplitJsonS3FilePath,
  clearDatasplitJsonS3FilePath,
  setDatasetImportMode,
  setDatasplitImportMode,
  snapshotIsAgentStepEnabled,
  setAnnotationAgentEnabled,
  setHumanSharePercentage,
  setAnnotationAgentModelId,
  setAnnotationAgentConcepts,
  saveAnnotationAgentConcept,
  saveAnnotationAgentSection,
  removeAnnotationAgentSection,
} = createProjectSlice.actions;

declare module '@verantos-dev/core-annotation-tool' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof createProjectSlice> {}
}

createProjectSlice.injectInto(rootReducer);
